import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter','Noto Sans JP',sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/27619c500cc1b52bbdf164e0a3e622eb/58ff4382ad3eba0a2a097f9160e1ae7b.png"
              alt="Visipo Logo"
              className="h-7 w-auto object-contain"
            />
            <span className="font-bold text-lg text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>Visipo</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-[#13B6EC] transition-colors flex items-center gap-1"
          >
            <i className="ri-arrow-left-line" />
            トップに戻る
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20">
        {/* Title */}
        <div className="mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-[#13B6EC] bg-[#13B6EC]/8 tracking-widest uppercase mb-4">
            PRIVACY POLICY
          </div>
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-4"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            プライバシーポリシー
          </h1>
          <p className="text-sm text-gray-400">制定日: 2026年4月5日</p>
        </div>

        {/* Policy content */}
        <div className="prose-policy">
          <p>
            本プライバシーポリシーは、開発者 Nagao.dev（以下「開発者」）が提供するスマートフォン向けアプリケーション「Visipo」（以下「本アプリ」）における、ユーザーの個人情報およびプライバシー情報の取り扱いについて定めるものです。
          </p>
          <p>
            本アプリは、Appleの「App Reviewガイドライン」等の要件を遵守し、ユーザーのプライバシー保護を最優先に開発・運用されています。
          </p>

          {/* Section 1 */}
          <h2>1. 取得するデータと利用目的</h2>
          <p>
            本アプリでは、各種機能の提供および体験のパーソナライズのため、以下のデータを取得・利用します。
          </p>

          <h3>(1) 連絡先情報・ID</h3>
          <ul>
            <li><strong>取得するデータの種類</strong>: メールアドレス、ユーザーID（認証プロバイダが付与する匿名ID）</li>
            <li><strong>利用目的</strong>: ユーザー認証、アカウントの管理（アプリの機能）</li>
            <li><strong>ユーザーとの紐付け</strong>: アカウントに紐付けられます。</li>
          </ul>

          <h3>(2) ヘルスケア／フィットネスデータ</h3>
          <ul>
            <li><strong>取得するデータの種類</strong>: 歩数データ（Apple HealthKit または Google Fit から取得）</li>
            <li><strong>利用目的</strong>: コースの踏破状況の計算、達成目標の判定、および日記の自動生成（アプリの機能、製品のパーソナライズ）</li>
            <li><strong>ユーザーとの紐付け</strong>: アカウントに紐付けられます。</li>
          </ul>

          <h3>(3) ユーザーコンテンツおよび設定情報</h3>
          <ul>
            <li><strong>取得するデータの種類</strong>: 本アプリ内で生成・保存される日記データ、ユーザーのプロフィール・設定情報（性別、生年月日、日記のトーン・ペルソナ設定等）。なお、性別および生年月日はユーザーが任意で入力するものであり、入力しなくても本アプリの主要機能は利用可能です。</li>
            <li><strong>利用目的</strong>: 日記の提供、保存、および文体のカスタマイズ（機能の提供、製品のパーソナライズ）</li>
            <li><strong>ユーザーとの紐付け</strong>: アカウントに紐付けられます。</li>
          </ul>

          <h3>(4) 決済およびサブスクリプション情報</h3>
          <ul>
            <li><strong>取得するデータの種類</strong>: サブスクリプションの購入履歴（レシートデータ）。クレジットカード番号等の決済に関する詳細情報はアプリストア（Apple / Google）が直接取得・管理し、開発者は取得・保存しません。</li>
            <li><strong>利用目的</strong>: 有料プランの提供および契約状況の確認、プレミアム機能の提供</li>
            <li><strong>ユーザーとの紐付け</strong>: アカウントまたはデバイスの匿名IDに紐付けられます。</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-[#13B6EC] p-4 rounded-r-lg my-6">
            <p className="text-sm text-gray-600 m-0">
              ※備考: 本アプリは、ユーザーの「位置情報（GPS等）」は一切収集・保存しません。また、診断データ（クラッシュログ等）について開発者が個別にユーザーと紐付けて収集することはありません。
            </p>
          </div>

          {/* Section 2 */}
          <h2>2. データの収集とサードパーティへの提供</h2>
          <p>
            本アプリでは、機能の提供（データの安全なクラウド保存、AIによるコンテンツ生成）を目的として、以下のサードパーティサービスを利用しています。これらのサービスを経由してデータが処理されますが、<strong>第三者の広告やマーケティング目的でデータが共有されることは一切ありません。</strong>
          </p>

          <div className="space-y-4 my-6">
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Firebase (Google LLC)</h4>
              <ul className="text-sm">
                <li><strong>利用目的</strong>: ユーザー認証（Firebase Authentication）、日記や進捗データのクラウド保存（Cloud Firestore）</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Google Generative AI API (Google LLC)</h4>
              <ul className="text-sm">
                <li><strong>利用目的</strong>: AIによる日記テキストの動的生成</li>
                <li><strong>送信されるデータ</strong>: 歩数の数値、ユーザーが設定画面で入力したプロフィール情報（性別、生年月日等）、日記の文体設定（ペルソナ・トーン）。なお、ユーザー名やメールアドレス等の直接的な個人識別情報はAI APIに送信されません。</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Google Cloud Text-to-Speech API (Google LLC)</h4>
              <ul className="text-sm">
                <li><strong>利用目的</strong>: 日記の音声読み上げ機能の提供</li>
                <li><strong>送信されるデータ</strong>: 日記テキスト、音声設定（声質・速度・ピッチ）</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">OpenWeatherMap API (OpenWeather Ltd)</h4>
              <ul className="text-sm">
                <li><strong>利用目的</strong>: 日記生成時の天気情報の取得</li>
                <li><strong>送信されるデータ</strong>: 緯度・経度の座標情報（アプリ内の仮想ルート上の到達地点の天気を取得するため）。なお、これはユーザーの実際の位置情報ではなく、アプリ内の地図上の架空の地点です。座標情報はAPI呼び出し時にのみ使用され、開発者のサーバーに保存されることはありません。</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Google Maps Geocoding API (Google LLC)</h4>
              <ul className="text-sm">
                <li><strong>利用目的</strong>: 仮想ルート上の到達地点の座標から地名（市区町村名等）への変換</li>
                <li><strong>送信されるデータ</strong>: 緯度・経度の座標情報（アプリ内の地図上の架空の地点）。なお、座標情報はAPI呼び出し時にのみ使用され、開発者のサーバーに保存されることはありません。</li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <h2>3. トラッキングおよび広告について</h2>
          <p>
            本アプリは、他社のアプリやウェブサイトを横断してユーザーを追跡する「トラッキング（Targeting/Tracking）」を一切行いません。また、取得したユーザーデータ（メールアドレスや歩数データを含むすべて）を、広告ネットワーク、データブローカー、その他の第三者へと提供、販売、共有することはありません。
          </p>

          {/* Section 4 */}
          <h2>4. HealthKit および Google Fit データの取り扱い</h2>
          <p>
            本アプリは、AppleのHealthKit APIおよびGoogleのGoogle Fit APIを使用して歩数等のヘルスケアデータを読み込みます。これらのデータは特に機密性が高い情報として、以下の厳格なルールの下で保護されます。
          </p>
          <ol>
            <li>取得したヘルスケアデータは、アプリの主要機能（歩数に基づくルート踏破と日記生成）を提供するためのみに使用されます。</li>
            <li>ヘルスケアデータを第三者の広告、広告ネットワーク、データブローカー等に提供、販売、トラッキング目的に使用することは一切ありません。</li>
          </ol>

          {/* Section 5 */}
          <h2>5. 未成年者のデータについて</h2>
          <p>
            本アプリは、13歳未満の児童を対象としたサービスではなく、13歳未満の児童から意図的に個人情報を収集することはありません。13歳未満の児童による本アプリの利用が判明した場合、開発者は該当するデータを速やかに削除します。
          </p>

          {/* Section 6 */}
          <h2>6. データの保存場所・保存期間</h2>
          <ol>
            <li>本アプリが収集したデータは、Google LLC が運営するクラウドサーバー（所在地：米国を含む日本国外の場合があります）上に保存されます。</li>
            <li>ユーザーの個人情報およびユーザーコンテンツは、ユーザーがアカウントを保有している期間中保存され、アカウント削除後は速やかに削除されます。</li>
          </ol>

          {/* Section 7 */}
          <h2>7. データの管理と削除</h2>
          <p>
            ユーザーは、本アプリの機能を通じて、いつでも自身の個人情報およびユーザーコンテンツにアクセスし、内容を確認することができます。
          </p>
          <p>
            また、ユーザーが本アプリ内の設定から「アカウント削除（退会）」手続きを行った場合、開発者のクラウドデータベース上に保存されている当該ユーザーのアカウント情報、歩数情報、日記データ等のすべての紐付けられたデータは、速やかに無効化・削除されます。
          </p>

          {/* Section 8 */}
          <h2>8. プライバシーポリシーの変更</h2>
          <p>
            開発者は、法令の変更、本アプリの機能追加や変更等に伴い、必要に応じて本プライバシーポリシーを改定することがあります。利用目的に関わる重要な変更がある場合は、変更の適用前にアプリ内での通知等、適切な方法でユーザーにお知らせし、必要な同意を再取得します。
          </p>

          {/* Section 9 */}
          <h2>9. お問い合わせ窓口</h2>
          <p>
            本プライバシーポリシー、または個人情報の取り扱いに関するご質問、データ削除に関するご依頼などがございましたら、下記のお問い合わせ先までご連絡ください。
          </p>
          <div className="bg-gray-50 rounded-xl p-5 my-4">
            <ul className="text-sm space-y-1">
              <li><strong>開発者</strong>: Nagao.dev</li>
              <li><strong>メールアドレス</strong>: support@visipo.com</li>
              <li>またはアプリ内の「お問い合わせ」メニューよりご連絡ください。</li>
            </ul>
          </div>
        </div>

        {/* Back button */}
        <div className="mt-16 pt-8 border-t border-gray-100 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#13B6EC] text-sm font-semibold hover:underline"
          >
            <i className="ri-arrow-left-line" />
            トップページに戻る
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center" style={{ background: "#1a2332" }}>
        <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
          &copy; 2026 Visipo. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
